import { Badge, Check, DataTable, formatDate, formatDateTime, formatMoney } from "@corecrm/ui";
import { Fragment } from "react";
import { useCompany } from "../api/queries";
import type { BillTo, Quote } from "../api/types";
import { QUOTE_KIND, quantity } from "../lib/labels";
import { choosable, groupsOf, pickOne, toggle } from "../lib/quoteOptions";
import { ProjectLink, Status, companyLine } from "./bits";
import "./quote.css";

type QuotePhase = Quote["phases"][number];
type QuoteLine = QuotePhase["lines"][number];

/** The client's picks among the quote's options while they can still accept it (UX-100). */
export interface Choosing {
  chosen: ReadonlySet<string>;
  onChoose: (next: Set<string>) => void;
}

/** A sent quote version laid out like its PDF: phases and lines, payment schedule, totals and
    terms. The prototype's quoteDoc(q, portal). Nothing internal is in the portal's schema.
    `showTerms` is off while the quote can be accepted: the acceptance panel shows them instead,
    next to the checkbox the client ticks to agree to them (US-5.9). With `choosing`, optional
    phases and lines get a checkbox and option groups a "Choose one" radio group (UX-100);
    without it, what's in and out is only labelled. Content blocks go before and after the
    phases (UX-114); the Statement of Work before the phases (UX-193). */
export function QuoteDocument({
  quote: q,
  showTerms = true,
  choosing,
}: {
  quote: Quote;
  showTerms?: boolean;
  choosing?: Choosing;
}) {
  const bill = q.bill_to as unknown as BillTo;
  const cr = q.kind === "change_request";
  const agreement = q.kind === "maintenance" || q.kind === "hosting";
  // A T&M quote's hours are estimates, so its totals are too (QA TM-03).
  const tm = q.kind === "time_and_materials";
  const hasMonthly = (q.monthly_total?.amount_minor ?? 0) > 0;
  // UX-192: a change request shown to someone who isn't a billing contact has no amounts.
  const amounts = q.total !== null;
  const company = useCompany().data;
  const phaseGroups = groupsOf(q.phases);
  // What's in: the client's picks while they choose (so it shows before the new totals come
  // back), otherwise as the API priced it.
  const phaseIn = (ph: QuotePhase) => (choosing && choosable(ph) ? choosing.chosen.has(ph.id) : ph.included);
  const lineIn = (ph: QuotePhase, l: QuoteLine) =>
    choosing ? phaseIn(ph) && (!choosable(l) || choosing.chosen.has(l.id)) : l.included;
  return (
    <article className="doc" aria-label={`${QUOTE_KIND[q.kind]} ${q.number}`}>
      <div className="doc-h">
        <div>
          <div className="eyebrow">{companyLine(company)}</div>
          <h2 style={{ fontSize: 20, marginTop: 4 }}>
            {QUOTE_KIND[q.kind]} {q.number} v{q.version_number}
          </h2>
          <div className="muted">{q.title}</div>
          {cr && q.project_id && (
            <div className="muted">
              Adds to project <ProjectLink id={q.project_id} />
            </div>
          )}
          <div className="faint">
            Bill to: {[bill?.name, ...(bill?.address ?? [])].filter(Boolean).join(", ") || "—"}
          </div>
        </div>
        <div className="doc-meta">
          <Status status={q.status} />
          <div className="faint">
            Issued {formatDate(q.sent_at)} · valid until {formatDate(q.valid_until)}
          </div>
          {q.total && <div className="faint">{q.total.currency}</div>}
        </div>
      </div>

      {q.introduction && <p style={{ whiteSpace: "pre-line" }}>{q.introduction}</p>}

      <Blocks blocks={q.blocks_before} />

      <StatementOfWork sow={q.statement_of_work} />

      {q.phases.map((ph, i) => {
        const inPhase = phaseIn(ph);
        const group = ph.option_group ? phaseGroups.get(ph.option_group) : undefined;
        return (
          <Fragment key={ph.id}>
            {choosing && group?.[0] === ph && (
              <OptionGroup
                legend={`Choose one: ${ph.option_group}`}
                name={`phase:${ph.option_group}`}
                options={group.map((p) => ({
                  id: p.id,
                  label: p.name,
                  price: formatMoney(p.option_one_time),
                }))}
                chosen={choosing.chosen}
                onPick={(id) => choosing.onChoose(pickOne(choosing.chosen, group, id))}
              />
            )}
            <section className={inPhase ? undefined : "qd-out"}>
              <h3>
                Phase {i + 1}: {ph.name}
                {cr && (
                  <span className="adds">
                    <Badge kind={ph.adds_to ? "info" : "acc"}>
                      {ph.adds_to ? `Adds to ${ph.adds_to}` : "New phase"}
                    </Badge>
                  </span>
                )}
                <OptionTag item={ph} included={inPhase} choosing={!!choosing} />
              </h3>
              {choosing && ph.optional && (
                <Check
                  checked={inPhase}
                  onChange={(on) => choosing.onChoose(toggle(choosing.chosen, ph.id, on))}
                >
                  Add {ph.name} (+{formatMoney(ph.option_one_time)})
                </Check>
              )}
              {ph.description && (
                <p className="muted" style={{ marginBottom: 8, whiteSpace: "pre-line" }}>
                  {ph.description}
                </p>
              )}
              <DataTable<QuoteLine>
                className="mid"
                label={`Phase ${i + 1}: ${ph.name}`}
                rows={ph.lines}
                rowKey={(l) => l.id}
                rowClassName={(l) => !lineIn(ph, l) && "qd-out"}
                columns={[
                  {
                    header: "Item",
                    cell: (l) => (
                      <>
                        {l.description}
                        {l.details_html && (
                          <div className="qd-details" dangerouslySetInnerHTML={{ __html: l.details_html }} />
                        )}
                        <OptionTag item={l} included={lineIn(ph, l)} choosing={!!choosing} />
                        {choosing && l.optional && (
                          <Check
                            checked={choosing.chosen.has(l.id)}
                            onChange={(on) => choosing.onChoose(toggle(choosing.chosen, l.id, on))}
                          >
                            Add this<span className="sr-only">: {l.description}</span>
                          </Check>
                        )}
                      </>
                    ),
                  },
                  {
                    header: "Qty",
                    align: "right",
                    cell: (l) => (
                      <span className="mono">
                        {quantity(l.quantity)}
                        {l.unit ? ` ${l.unit}${Number(l.quantity) === 1 ? "" : "s"}` : ""}
                      </span>
                    ),
                  },
                  {
                    header: "Unit price",
                    align: "right",
                    cell: (l) => (
                      <span className="mono">{`${formatMoney(l.unit_price)}${l.recurring ? "/mo" : ""}`}</span>
                    ),
                  },
                  {
                    header: "Discount",
                    align: "right",
                    cell: (l) => (l.discount_percent ? `${Number(l.discount_percent)}%` : null),
                  },
                  {
                    header: "Amount",
                    align: "right",
                    role: "key",
                    cell: (l) => (
                      <span className={lineIn(ph, l) ? "mono" : "mono qd-struck"}>
                        {`${formatMoney(l.net)}${l.recurring ? "/mo" : ""}`}
                      </span>
                    ),
                  },
                ]}
                foot={[
                  "Phase subtotal",
                  null,
                  null,
                  null,
                  inPhase ? (
                    <span className="mono">
                      {formatMoney(ph.one_time)}
                      {!!ph.monthly && ph.monthly.amount_minor > 0 && ` + ${formatMoney(ph.monthly)}/mo`}
                    </span>
                  ) : (
                    "Not included"
                  ),
                ]}
              />
              {choosing &&
                [...groupsOf(ph.lines)].map(([name, members]) => (
                  <OptionGroup
                    key={name}
                    legend={`Choose one: ${name}`}
                    name={`${ph.id}:${name}`}
                    options={members.map((l) => ({
                      id: l.id,
                      label: l.description,
                      price: `${formatMoney(l.net)}${l.recurring ? "/mo" : ""}`,
                    }))}
                    chosen={choosing.chosen}
                    onPick={(id) => choosing.onChoose(pickOne(choosing.chosen, members, id))}
                  />
                ))}
            </section>
          </Fragment>
        );
      })}

      {!amounts && (
        <p className="note" role="note">
          Amounts are shown only to your account's billing contacts.
        </p>
      )}

      {/* Totals, then Before the milestones, the schedule, Before the terms (UX-154). */}
      {amounts && (
        <div className="grid2" style={{ marginTop: 18 }}>
          <div />
          <div className="totals" aria-label="Totals">
            <div className="ln">
              <span>{tm ? "Estimated subtotal" : "Subtotal"}</span>
              <span>{formatMoney(q.subtotal)}</span>
            </div>
            {!!q.discount && q.discount.amount_minor > 0 && (
              <div className="ln">
                <span>Discount</span>
                <span>−{formatMoney(q.discount)}</span>
              </div>
            )}
            {q.tax_lines.length > 1 ? (
              q.tax_lines.map((t) => (
                <div className="ln" key={t.label}>
                  <span>{t.label}</span>
                  <span>{formatMoney(t.amount)}</span>
                </div>
              ))
            ) : (
              <div className="ln">
                <span>{q.tax_label}</span>
                <span>{formatMoney(q.tax)}</span>
              </div>
            )}
            <div className="ln grand">
              <span>{tm ? "Estimated total" : "Total"}</span>
              <span>{formatMoney(q.total)}</span>
            </div>
            {hasMonthly && (
              <>
                <div className="ln rec">
                  <span>Monthly</span>
                  <span>{formatMoney(q.monthly_subtotal)}</span>
                </div>
                {q.tax_lines.length > 1 ? (
                  q.tax_lines.map((t) => (
                    <div className="ln" key={t.label}>
                      <span>{t.label}</span>
                      <span>{formatMoney(t.monthly_amount)}</span>
                    </div>
                  ))
                ) : (
                  <div className="ln">
                    <span>{q.tax_label}</span>
                    <span>{formatMoney(q.monthly_tax)}</span>
                  </div>
                )}
                <div className="ln grand">
                  <span>Monthly total</span>
                  <span>{formatMoney(q.monthly_total)}/mo</span>
                </div>
              </>
            )}
            {q.tax_note && <p className="faint">{q.tax_note}</p>}
            {q.tax_lines.some((t) => t.registration_number) && (
              <p className="faint">
                {q.tax_lines
                  .filter((t) => t.registration_number)
                  .map((t) => `${t.label.split(" ")[0]} no. ${t.registration_number}`)
                  .join(" · ")}
              </p>
            )}
          </div>
        </div>
      )}

      <Blocks blocks={q.blocks_milestones} />

      {amounts && (
        <section style={{ marginTop: 18 }} aria-label="Payment schedule">
          <h3 style={{ marginTop: 0 }}>Payment schedule</h3>
          {q.milestones.length ? (
            <div className="item-list">
              {q.milestones.map((m, i) => (
                <div className="item" key={i}>
                  <div className="what">
                    <strong>{m.label}</strong>
                    <span>
                      {m.when}
                      {m.percent ? ` · ${Number(m.percent)}%` : ""}
                    </span>
                  </div>
                  <span className="mono">{formatMoney(m.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">
              {agreement
                ? "Billed in advance in installments, as the terms set out."
                : "Billed from logged hours."}
            </p>
          )}
        </section>
      )}

      <Blocks blocks={q.blocks_terms} />

      {/* UX-194: the warranty the project gets once it's finished and closed. */}
      {!agreement && q.warranty && q.warranty !== "no warranty" && (
        <p className="muted" aria-label="Warranty">
          Warranty: <strong>{q.warranty}</strong> after the project is closed.
        </p>
      )}

      {showTerms && q.terms && (
        <section aria-label="Terms and conditions">
          <h3>Terms and conditions</h3>
          <p className="muted" style={{ whiteSpace: "pre-line" }}>
            {q.terms}
          </p>
        </section>
      )}

      {q.status === "accepted" && (
        <div className="note acc" style={{ marginTop: 14 }}>
          Accepted on {formatDateTime(q.decided_at)} with a typed signature.
        </div>
      )}
      {q.status === "declined" && (
        <div className="note bad" style={{ marginTop: 14 }}>
          Declined on {formatDateTime(q.decided_at)}.
        </div>
      )}
    </article>
  );
}

/** The Statement of Work (UX-193): numbered scope entries, then In scope, Out of scope and
    Assumptions (those written), all server-sanitized HTML. Nothing when the quote has none. */
export function StatementOfWork({ sow }: { sow: Quote["statement_of_work"] }) {
  if (!sow) return null;
  return (
    <section className="qd-block qd-sow" aria-label="Statement of Work">
      <h3>Statement of Work</h3>
      {sow.entries.length > 0 && (
        <ol className="sow-entries">
          {sow.entries.map((e, i) => (
            <li key={i}>
              {/* From a feature request (UX-196): its key before the title. */}
              {e.feature_request_key && <span className="mono faint">{e.feature_request_key} </span>}
              <strong>{e.title}</strong>
              {/* Its estimate, in hours only (UX-196b). */}
              {e.estimate_hours !== null && e.estimate_hours !== undefined && (
                <span className="faint"> (estimate {Number(e.estimate_hours)} h)</span>
              )}
              {e.description_html && (
                <div
                  className="qd-block-body faint"
                  dangerouslySetInnerHTML={{ __html: e.description_html }}
                />
              )}
            </li>
          ))}
        </ol>
      )}
      {sow.sections.map((s) => (
        <div key={s.key}>
          <h4>{s.label}</h4>
          <div className="qd-block-body" dangerouslySetInnerHTML={{ __html: s.body }} />
        </div>
      ))}
    </section>
  );
}

/** A quote's content blocks (UX-114): a heading and server-sanitized HTML. */
function Blocks({ blocks }: { blocks: Quote["blocks_before"] }) {
  return blocks.map((b, i) => (
    <section className="qd-block" key={i}>
      {b.title && <h3>{b.title}</h3>}
      <div className="qd-block-body" dangerouslySetInnerHTML={{ __html: b.body }} />
    </section>
  ));
}

/** Whether an optional or grouped phase or line is in. An optional one the client is choosing
    has its checkbox instead. */
function OptionTag({
  item,
  included,
  choosing,
}: {
  item: QuotePhase | QuoteLine;
  included: boolean;
  choosing: boolean;
}) {
  let text: string | null = null;
  if (item.option_group) text = `Option: ${item.option_group} — ${included ? "selected" : "alternative"}`;
  else if (item.optional && !choosing) text = included ? "Optional — included" : "Optional — not included";
  if (!text) return null;
  return (
    <span className="adds qd-tag">
      <Badge kind={included ? "acc" : "mute"}>{text}</Badge>
    </span>
  );
}

/** An option group as radio buttons, each choice with what it costs. */
function OptionGroup({
  legend,
  name,
  options,
  chosen,
  onPick,
}: {
  legend: string;
  name: string;
  options: { id: string; label: string; price: string }[];
  chosen: ReadonlySet<string>;
  onPick: (id: string) => void;
}) {
  return (
    <fieldset className="qd-options">
      <legend>{legend}</legend>
      {options.map((o) => (
        <label className="check qd-option" key={o.id}>
          <input type="radio" name={name} checked={chosen.has(o.id)} onChange={() => onPick(o.id)} />
          <span>{o.label}</span>
          <span className="mono qd-price">{o.price}</span>
        </label>
      ))}
    </fieldset>
  );
}
